import { useState, useEffect, useRef, useCallback, useMemo, type MutableRefObject } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Form } from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreVertical } from 'lucide-react';
import {
  Loader2,
  Save,
  Clock,
  Zap,
  Home,
  Settings,
  GitBranch,
  FileText,
  FileSearch,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  Check,
  ClipboardList,
  Download,
  FileDown,
  Hand,
  Smartphone,
  Package,
} from 'lucide-react';
import { ProjectInfo } from './survey-sections/project-info';
import { ElectricalSupply } from './survey-sections/electrical-supply';
import { RoofInformation } from './survey-sections/roof-information';
import { AcInstallation } from './survey-sections/ac-installation';
import { EquipmentLocation } from './survey-sections/equipment-location';
import { DcAdditionalWorks } from './survey-sections/dc-additional-works';
import { SurveySummary } from './survey-sections/survey-summary';
import { BillOfMaterials } from './survey-sections/bill-of-materials';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { useSwipeable } from 'react-swipeable';
import { useHotkeys } from 'react-hotkeys-hook';
import { siteSurveyProgress } from '@/lib/site-survey-progress';
import { PrintToPDF } from './print-to-pdf';
import { PrintService } from '../../services/print-service';
import {
  detectDevice,
  getSwipeConfig,
  shouldEnableSwipe,
  setSwipePreference,
  getSwipePreference,
  getSwipeHintMessage
} from '@/lib/device-detection';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

// Enhanced Site Survey Schema with better validation
const siteSurveySchema = z.object({
  // Core IDs
  surveyId: z.number().optional(),
  customerId: z.number(),
  installationId: z.number().optional(),

  // Project Tab (Tab 1) - Enhanced with separate address fields
  siteAssessorName: z.string().optional(),
  surveyDate: z.string().optional().default(new Date().toISOString().split('T')[0]),
  clientName: z.string().optional(),
  clientAddress: z.string().optional(),
  clientCity: z.string().optional(),
  clientPostCode: z.string().optional(),
  clientTelephone: z.string().optional(),
  clientEmail: z.string().optional(),

  // Equipment details
  panelsDescription: z.string().optional(),
  panelsQuantity: z.number().optional(),
  panels: z.array(z.object({
    id: z.string(),
    description: z.string().optional(),
    quantity: z.number().optional().default(1),
    highlighted: z.boolean().optional().default(false),
  })).optional().default([]),
  panelDimensions: z.object({
    width: z.number().optional(),
    length: z.number().optional(),
    depth: z.number().optional(),
    unit: z.string().optional(),
  }).optional(),
  inverter1: z.string().optional(),
  inverter2: z.string().optional(),
  inverter3: z.string().optional(),
  // Dynamic batteries array - supports multiple batteries with individual highlighting
  batteries: z.array(z.object({
    id: z.string(),
    description: z.string().optional(),
    quantity: z.number().optional().default(1),
    highlighted: z.boolean().optional().default(false),
  })).optional().default([]),
  // Legacy fields for backward compatibility
  batteryDescription: z.string().optional(),
  batteryQuantity: z.number().optional(),
  propertyType: z.string().optional(),

  // Images
  stringPlanFromOpenSolar: z.string().optional(),
  aerialImageFromDrone: z.string().optional(),
  generalImage1: z.string().optional(),
  generalImage2: z.string().optional(),
  generalImage3: z.string().optional(),

  // Roof Information
  roofs: z
    .array(
      z.object({
        roofType: z
          .enum(['concrete_tile', 'plain_tile', 'slate', 'flat', 'trap', 'other'])
          .optional(),
        notes: z.string().optional(),
        pitch: z.number().optional(),
        roofImage1: z.string().optional(),
        roofImage2: z.string().optional(),
        roofImage3: z.string().optional(),
        membrane: z.enum(['none', 'new', 'good', 'poor']).optional(),
        membraneImage1: z.string().optional(),
        membraneImage2: z.string().optional(),
        existingFaults: z.enum(['client_acknowledgment', 'none', 'yes_required']).optional(),
        optimisers: z.enum(['quoted', 'required', 'no']).optional(),
      })
    )
    .default([{}]),
  scopeOfWorksRoof: z.string().optional(),
  scaffolding: z.enum(['required', 'not_required', 'domelec_tower']).optional(),
  scaffoldingWidth: z.number().optional(),
  scaffoldingLifts: z.number().optional(),
  scaffoldingNotes: z.string().optional(),
  scaffoldPlanImage: z.string().optional(),

  // Electrical Supply Tab
  phases: z.string().optional(),
  mainFuseSize: z.string().optional(),
  bondingWater: z.string().optional(),
  bondingGas: z.string().optional(),
  earthingSystem: z.string().optional(),
  acSpdPresent: z.string().optional(),
  eicr: z.string().optional(),
  spaceAvailableForPvDb: z.string().optional(),
  photoIncomer: z.string().optional(),
  photoMeter: z.string().optional(),
  photoMainDb: z.string().optional(),
  photoOther: z.string().optional(),

  // AC Installation
  inverterDb: z.string().optional(),
  acSize: z.string().optional(),
  switchFuseRequired: z.string().optional(),
  exportLimitation: z.string().optional(),
  inverterCircuits: z
    .array(
      z.object({
        id: z.string().optional(),
        type: z.string().optional(),
        cca: z.string().optional(),
        length: z.string().optional(),
        faultProtection: z.string().optional(),
        deviceType: z.string().optional(),
        notes: z.string().optional(),
      })
    )
    .default([]),
  cableRouteImage1: z.string().optional(),
  cableRouteImage2: z.string().optional(),

  // GPS Location
  surveyLatitude: z.number().optional(),
  surveyLongitude: z.number().optional(),
  surveyAddress: z.string().optional(),

  // Equipment Location
  proposedArea: z.string().optional(),
  proposedAreaNotes: z.string().optional(),
  proposedAreaPhoto: z.string().optional(),
  overboardWall: z.string().optional(),
  canopy: z.string().optional(),
  equipmentHeight: z.string().optional(),
  equipmentWidth: z.string().optional(),
  equipmentNotes: z.string().optional(),
  scopeOfWorksInverterAC: z.string().optional(),

  // DC & Additional Works
  dcStrings: z.string().optional(),
  dcCableCsa: z.string().optional(),
  dcLongestRun: z.string().optional(),
  dcRouteImage1: z.string().optional(),
  dcRouteImage2: z.string().optional(),
  dcRouteImage3: z.string().optional(),
  scopeOfWorksDcRuns: z.string().optional(),

  // Other Works
  evChargerRequired: z.boolean().default(false),
  evChargerDetails: z.string().optional(),
  immersionCircuitRequired: z.boolean().default(false),
  immersionCircuitDetails: z.string().optional(),
  birdguardRequired: z.boolean().default(false),
  birdguardDetails: z.string().optional(),
  gatewayRequired: z.boolean().default(false),
  gatewayDetails: z.string().optional(),

  // Earthing and Ground Works
  gatewayEarthing: z.string().optional(),
  earthPitLocationImage: z.string().optional(),
  groundTools: z.string().optional(),
  diggerHire: z.string().optional(),
  scopeOfWorksGroundWorks: z.string().optional(),

  // Summary
  specialRequirements: z.string().optional(),
  
  // Highlight metadata for tracking highlighted fields (not stored in DB)
  highlightedFields: z.record(z.boolean()).optional().default({}),
});

type SiteSurveyFormData = z.infer<typeof siteSurveySchema>;

interface SiteSurveyFormProps {
  customerId?: number;
  installationId?: number;
  surveyId?: number;
  initialData?: Partial<SiteSurveyFormData>;
  onSave?: (data: SiteSurveyFormData) => void;
  onComplete?: (data: SiteSurveyFormData) => void;
  onChange?: (data: SiteSurveyFormData) => void;
  onTabChange?: (newTab: string) => void;
  onPhotoUploaded?: () => void;
  isSaving?: boolean;
  isCompleting?: boolean;
}

interface TabConfig {
  id: string;
  label: string;
  shortLabel: string;
  icon: React.ElementType;
  requiredFields?: string[];
  description?: string;
}

export function SiteSurveyFormOptimized({
  customerId,
  installationId,
  surveyId,
  initialData,
  onSave,
  onComplete,
  onChange,
  onTabChange,
  onPhotoUploaded,
  isSaving,
  isCompleting,
}: SiteSurveyFormProps) {
  const [activeTab, setActiveTab] = useState('project');
  const [surveyStartTime, setSurveyStartTime] = useState<Date | null>(null);
  const [completedTabs, setCompletedTabs] = useState<Set<string>>(new Set());
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isFormInteracting, setIsFormInteracting] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [swipeEnabled, setSwipeEnabled] = useState(shouldEnableSwipe());
  const [showSwipeHint, setShowSwipeHint] = useState(true);
  const { toast } = useToast();
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const formContainerRef = useRef<HTMLDivElement | null>(null);

  const form = useForm<SiteSurveyFormData>({
    resolver: zodResolver(siteSurveySchema),
    defaultValues: {
      surveyId: surveyId,
      customerId: customerId || 0,
      installationId: installationId,
      roofs: [{}],
      inverterCircuits: [
        {
          id: Date.now().toString(),
          type: '',
          cca: '',
          length: '',
          faultProtection: '',
          deviceType: '',
          notes: '',
        },
      ],
      panels: initialData?.panels || [],
      batteries: initialData?.batteries || [], // Use initialData batteries if available, otherwise empty
      evChargerRequired: initialData?.evChargerRequired ?? false,
      immersionCircuitRequired: initialData?.immersionCircuitRequired ?? false,
      birdguardRequired: initialData?.birdguardRequired ?? false,
      gatewayRequired: initialData?.gatewayRequired ?? false,
      highlightedFields: initialData?.highlightedFields || {},  // Use initialData highlightedFields if available
      ...initialData,
    },
  });

  // Track if form has been initialized to prevent overwriting user input
  const formInitialized = useRef(false);
  const initialDataRef = useRef(initialData);

  // Update form when initialData changes, but only on first load or if form is empty
  useEffect(() => {
    // Check if we have new data that differs from what was previously loaded
    const hasNewData =
      initialData &&
      Object.keys(initialData).length > 0 &&
      JSON.stringify(initialData) !== JSON.stringify(initialDataRef.current);

    // Skip if no new data to load
    if (!hasNewData) {
      return;
    }

    console.log('[SiteSurveyForm] Loading initial data into form:', initialData);

    if (initialData && Object.keys(initialData).length > 0) {
      // First, reset the form with all the data at once
      form.reset({
        ...form.getValues(), // Keep existing values
        ...initialData, // Override with new initial data
        // Ensure IDs are preserved
        surveyId: initialData.surveyId || surveyId,
        customerId: initialData.customerId || customerId,
        installationId: initialData.installationId || installationId,
      });

      // Explicitly ensure highlightedFields is properly set after reset
      if (initialData.highlightedFields && Object.keys(initialData.highlightedFields).length > 0) {
        console.log('[SiteSurveyForm] Setting highlightedFields from initialData:', initialData.highlightedFields);
        form.setValue('highlightedFields', initialData.highlightedFields, {
          shouldValidate: false,
          shouldDirty: false,
        });
      }

      // Explicitly ensure panels array is properly set after reset
      // This is critical for backward compatibility with legacy panel fields
      if (initialData.panels && Array.isArray(initialData.panels) && initialData.panels.length > 0) {
        console.log('[SiteSurveyForm] Setting panels from initialData:', initialData.panels);
        form.setValue('panels', initialData.panels, {
          shouldValidate: false,
          shouldDirty: false,
        });
      }

      // Explicitly ensure batteries array is properly set after reset
      // This is critical for backward compatibility with legacy battery fields
      if (initialData.batteries && Array.isArray(initialData.batteries) && initialData.batteries.length > 0) {
        console.log('[SiteSurveyForm] Setting batteries from initialData:', initialData.batteries);
        form.setValue('batteries', initialData.batteries, {
          shouldValidate: false,
          shouldDirty: false,
        });
      }

      // Then handle special cases for complex fields
      Object.keys(initialData).forEach((key) => {
        const value = initialData[key as keyof SiteSurveyFormData];

        // Skip image fields that might have stale URLs
        const imageFields = [
          'stringPlanFromOpenSolar',
          'aerialImageFromDrone',
          'generalImage1',
          'generalImage2',
          'generalImage3',
          'photoIncomer',
          'photoMeter',
          'photoMainDb',
          'photoOther',
          'roofImage1',
          'roofImage2',
          'roofImage3',
          'membraneImage1',
          'membraneImage2',
          'scaffoldPlanImage',
          'cableRouteImage1',
          'cableRouteImage2',
          'proposedAreaPhoto',
          'dcRouteImage1',
          'dcRouteImage2',
          'dcRouteImage3',
          'earthPitLocationImage',
        ];

        // Handle boolean fields (checkboxes) explicitly
        const booleanFields = [
          'evChargerRequired',
          'immersionCircuitRequired',
          'birdguardRequired',
        ];

        // For image fields, only set if they're valid URLs
        if (imageFields.includes(key) && typeof value === 'string') {
          // Keep any non-empty URL, including API endpoints
          if (value && value !== '') {
            // Don't filter out API endpoints or relative URLs
            form.setValue(key as keyof SiteSurveyFormData, value);
          } else {
            // Explicitly clear the field only if truly empty
            form.setValue(key as keyof SiteSurveyFormData, '');
          }
        } else if (booleanFields.includes(key)) {
          // For boolean fields, ensure we have a proper boolean value
          form.setValue(key as keyof SiteSurveyFormData, !!value);
        } else if (value !== undefined && value !== null) {
          form.setValue(key as keyof SiteSurveyFormData, value);
        }
      });

      formInitialized.current = true;
      initialDataRef.current = initialData;

      console.log('[SiteSurveyForm] Form data after loading:', form.getValues());
    }
  }, [initialData, form, surveyId, customerId, installationId]);

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Start timer when survey begins
  useEffect(() => {
    if (!surveyStartTime) {
      setSurveyStartTime(new Date());
    }
  }, [surveyStartTime]);

  // Notify parent of form changes
  useEffect(() => {
    const subscription = form.watch((value) => {
      const data = value as SiteSurveyFormData;
      onChange?.(data);
      // Removed auto-save here since parent component handles it
    });
    return () => subscription.unsubscribe();
  }, [form, onChange]);

  // Tab configuration with validation
  const tabs: TabConfig[] = [
    {
      id: 'project',
      label: 'Project',
      shortLabel: 'Project',
      icon: FileSearch,
      requiredFields: ['siteAssessorName', 'clientName', 'clientAddress'],
      description: 'Basic info',
    },
    {
      id: 'electrical',
      label: 'Electrical',
      shortLabel: 'Electrical',
      icon: Zap,
      requiredFields: ['phases', 'mainFuseSize'],
      description: 'System details',
    },
    {
      id: 'roof',
      label: 'Roof',
      shortLabel: 'Roof',
      icon: Home,
      requiredFields: ['scaffolding'],
      description: 'Structure',
    },
    {
      id: 'ac-equipment',
      label: 'AC/Equipment',
      shortLabel: 'AC/Eq',
      icon: Settings,
      requiredFields: ['inverterDb', 'proposedArea'],
      description: 'Installation',
    },
    {
      id: 'dc-works',
      label: 'DC/Works',
      shortLabel: 'DC',
      icon: GitBranch,
      requiredFields: ['dcStrings', 'dcCableCsa', 'dcLongestRun'],
      description: 'Additional',
    },
    {
      id: 'summary',
      label: 'Summary',
      shortLabel: 'Summary',
      icon: FileText,
      requiredFields: [],
      description: 'Review',
    },
    {
      id: 'bom',
      label: 'Bill of Materials',
      shortLabel: 'BOM',
      icon: Package,
      requiredFields: [],
      description: 'Materials',
    },
  ];

  // Watch form changes without creating memory leak
  const formValues = form.watch();

  // Use unified progress calculation from the service
  const progressResult = useMemo(() => {
    console.log('[Survey Form] Calculating progress with formValues:', {
      hasFormValues: !!formValues,
      surveyDate: formValues?.surveyDate,
      siteAssessorName: formValues?.siteAssessorName,
      mainFuseSize: formValues?.mainFuseSize,
      acSpdPresent: formValues?.acSpdPresent,
      earthingSystem: formValues?.earthingSystem,
    });

    const result = siteSurveyProgress.calculateProgress(formValues);

    // Always log progress calculation for debugging
    console.log('[Survey Form Debug] Progress result:', {
      percentage: result.percentage,
      canComplete: result.canComplete,
      completedItems: result.completedItems,
      totalItems: result.totalItems,
      missingFieldsCount: result.missingFields?.length || 0,
      missingFields: result.missingFields?.slice(0, 10), // Show first 10 missing fields
    });

    // Debug logging for completion issues
    if (result.percentage < 100 && formValues.surveyDate) {
      console.log('[Survey Form Debug] Incomplete survey details:', {
        surveyDate: formValues.surveyDate,
        surveyDateType: typeof formValues.surveyDate,
        acSpdPresent: formValues.acSpdPresent,
        allMissingFields: result.missingFields,
      });
    }

    return result;
  }, [formValues]); // Recalculate on form changes

  // Check if a tab is complete based on actual data, not navigation
  const isTabComplete = (tabId: string) => {
    const formData = form.getValues();
    const isComplete = siteSurveyProgress.isSectionComplete(tabId, formData);

    // Debug logging only in development
    if (
      process.env.NODE_ENV === 'development' &&
      (tabId === 'ac-equipment' || tabId === 'dc-works')
    ) {
      console.log(`[Tab Completion Debug] ${tabId}:`, {
        isComplete,
        relevantFields:
          tabId === 'ac-equipment'
            ? {
                inverterDb: formData.inverterDb,
                acSize: formData.acSize,
                switchFuseRequired: formData.switchFuseRequired,
                proposedArea: formData.proposedArea,
                equipmentHeight: formData.equipmentHeight,
                equipmentWidth: formData.equipmentWidth,
              }
            : {
                dcStrings: formData.dcStrings,
                dcCableCsa: formData.dcCableCsa,
                dcLongestRun: formData.dcLongestRun,
                gatewayEarthing: formData.gatewayEarthing,
              },
      });
    }

    return isComplete;
  };

  // Progress is now calculated directly from progressResult

  // Auto-mark Summary tab as complete when all required fields are filled
  useEffect(() => {
    if (progressResult.percentage === 100 && !completedTabs.has('summary')) {
      setCompletedTabs((prev) => new Set([...prev, 'summary']));
    }
  }, [progressResult.percentage, completedTabs]);

  // Navigate between tabs
  const navigateTab = (direction: 'prev' | 'next') => {
    const currentIndex = tabs.findIndex((tab) => tab.id === activeTab);

    if (direction === 'prev' && currentIndex > 0) {
      const newTab = tabs[currentIndex - 1].id;
      setActiveTab(newTab);
      onTabChange?.(newTab);
    } else if (direction === 'next' && currentIndex < tabs.length - 1) {
      // Mark current tab as visited
      setCompletedTabs((prev) => new Set([...prev, activeTab]));
      const newTab = tabs[currentIndex + 1].id;
      setActiveTab(newTab);
      onTabChange?.(newTab);
    }

    // Smooth scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Keyboard navigation
  useHotkeys('ctrl+right, cmd+right', () => navigateTab('next'), [activeTab]);
  useHotkeys('ctrl+left, cmd+left', () => navigateTab('prev'), [activeTab]);
  useHotkeys(
    'ctrl+s, cmd+s',
    (e) => {
      e.preventDefault();
      handleSave();
    },
    [form]
  );

  // Detect device type and get appropriate swipe configuration
  const device = useMemo(() => detectDevice(), []);
  const swipeConfig = useMemo(() => getSwipeConfig(device.type, swipeEnabled), [device.type, swipeEnabled]);

  // Track form interactions to prevent accidental swipes
  useEffect(() => {
    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        setIsFormInteracting(true);
      }
    };

    const handleFocusOut = () => {
      // Small delay to handle focus transitions between form fields
      setTimeout(() => {
        const activeElement = document.activeElement as HTMLElement;
        if (!activeElement || (activeElement.tagName !== 'INPUT' && activeElement.tagName !== 'TEXTAREA' && activeElement.tagName !== 'SELECT')) {
          setIsFormInteracting(false);
        }
      }, 100);
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  // Detect scrolling to prevent swipe conflicts
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolling(true);

      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Set new timeout to reset scrolling state
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 150); // 150ms after scrolling stops
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Also listen on the form container if it exists
    const container = formContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Show swipe hint on first load for touch devices
  useEffect(() => {
    if (device.isTouch && swipeEnabled && showSwipeHint) {
      const hasSeenHint = localStorage.getItem('siteSurvey.swipeHintSeen');
      if (!hasSeenHint) {
        toast({
          title: '💡 Tip',
          description: getSwipeHintMessage(device.type),
          duration: 5000,
        });
        localStorage.setItem('siteSurvey.swipeHintSeen', 'true');
        setShowSwipeHint(false);
      }
    }
  }, [device, swipeEnabled, showSwipeHint, toast]);

  // Enhanced swipe handlers with adaptive configuration
  const { ref: swipeRef, ...swipeHandlers } = useSwipeable({
    onSwipedLeft: () => {
      // Prevent swipe if form is being interacted with or scrolling
      if (!isFormInteracting && !isScrolling && swipeEnabled) {
        console.log('[Swipe] Left swipe detected, navigating to next tab');
        navigateTab('next');
      }
    },
    onSwipedRight: () => {
      // Prevent swipe if form is being interacted with or scrolling
      if (!isFormInteracting && !isScrolling && swipeEnabled) {
        console.log('[Swipe] Right swipe detected, navigating to previous tab');
        navigateTab('prev');
      }
    },
    onSwiping: (eventData) => {
      // Log swiping for debugging on tablets
      if (device.type === 'tablet') {
        console.log('[Swipe] Swiping:', {
          deltaX: eventData.deltaX,
          deltaY: eventData.deltaY,
          velocity: eventData.velocity,
        });
      }
    },
    delta: swipeConfig.delta,
    preventScrollOnSwipe: true,
    trackTouch: true,
    trackMouse: false, // Never track mouse to avoid conflicts
    rotationAngle: swipeConfig.rotationAngle,
    swipeDuration: swipeConfig.swipeDuration,
  });

  // Handle swipe preference toggle
  const handleSwipeToggle = (enabled: boolean) => {
    setSwipeEnabled(enabled);
    setSwipePreference(enabled);

    toast({
      title: enabled ? 'Swipe Navigation Enabled' : 'Swipe Navigation Disabled',
      description: enabled
        ? `Swipe gestures are now active. ${getSwipeHintMessage(device.type)}`
        : 'Use the navigation buttons to move between sections.',
      duration: 3000,
    });
  };

  const onSubmit = async (data: SiteSurveyFormData) => {
    try {
      console.log('[Survey Form] Submitting form for completion:', data);
      await onComplete?.(data);
    } catch (error) {
      console.error('[Survey Form] Error completing survey:', error);
      toast({
        title: 'Error',
        description: 'Failed to complete survey. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleSave = () => {
    const data = form.getValues();
    onSave?.(data);
    setLastSaved(new Date());
    toast({
      title: 'Survey Saved',
      description: 'Your progress has been saved.',
      duration: 2000,
    });
  };

  const handleExportPdf = async () => {
    if (!surveyId) {
      toast({
        title: 'Survey Not Saved',
        description: 'Please save the survey before generating a PDF.',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Use the new print service for PDF generation with in-form highlight overrides
      await PrintService.printSurvey(surveyId.toString(), undefined, {
        highlightedFields: form.getValues('highlightedFields') || {},
      });

      toast({
        title: 'Print Dialog Opened',
        description: "Use your browser's print dialog to save as PDF or print.",
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: 'Export Failed',
        description:
          error instanceof Error ? error.message : 'Failed to generate PDF. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Use the unified progress result
  const progress = progressResult.percentage;

  // Debug form submission
  console.log('[Survey Form Render]', {
    isSaving,
    canComplete: progressResult.canComplete,
    percentage: progressResult.percentage,
    buttonShouldBeEnabled: !isSaving && progressResult.canComplete,
  });

  return (
    <div
      ref={(el) => {
        // Combine both refs
        formContainerRef.current = el;
        if (typeof swipeRef === 'function') {
          swipeRef(el);
        } else if (swipeRef) {
          (swipeRef as MutableRefObject<HTMLDivElement | null>).current = el;
        }
      }}
      className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6"
      {...swipeHandlers}
    >
      {/* Compact Progress Header */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b">
        <div className="py-2 sm:py-3">
          {/* Progress Bar */}
          <div className="mb-2">
            <Progress value={progress} className="h-1.5" />
          </div>

          {/* Tab Navigation - Mobile Optimized */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 overflow-x-auto scrollbar-hide">
              <div className="flex gap-1 sm:gap-2 min-w-max px-1">
                {tabs.map((tab) => {
                  const isComplete = isTabComplete(tab.id);
                  const isCurrent = tab.id === activeTab;

                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        onTabChange?.(tab.id);
                      }}
                      className={cn(
                        'relative flex items-center gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all min-w-fit',
                        'hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring',
                        isCurrent && 'bg-primary text-primary-foreground shadow-sm',
                        !isCurrent && isComplete && 'bg-muted text-muted-foreground',
                        !isCurrent && !isComplete && 'text-muted-foreground'
                      )}
                    >
                      <tab.icon
                        className={cn(
                          'h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0',
                          isComplete && 'text-green-600'
                        )}
                      />
                      <span className="whitespace-nowrap">{tab.shortLabel}</span>
                      {isComplete && (
                        <Check className="h-3 w-3 text-green-600 absolute -top-1 -right-1 bg-background rounded-full" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Status Indicators */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {/* Auto-save indicator */}
              <div className="flex items-center gap-1">
                {autoSaving ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : lastSaved ? (
                  <Check className="h-3 w-3 text-green-600" />
                ) : null}
                <span className="hidden sm:inline">
                  {autoSaving ? 'Saving...' : lastSaved ? 'Saved' : ''}
                </span>
              </div>

              {/* Progress */}
              <Badge variant="secondary" className="text-xs">
                {progress}%
              </Badge>

              {/* Swipe Toggle for Touch Devices */}
              {device.isTouch && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      title="Navigation Settings"
                    >
                      {swipeEnabled ? (
                        <Hand className="h-3.5 w-3.5" />
                      ) : (
                        <Smartphone className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72">
                    <div className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="swipe-toggle" className="text-sm font-medium">
                          Swipe Navigation
                        </Label>
                        <Switch
                          id="swipe-toggle"
                          checked={swipeEnabled}
                          onCheckedChange={handleSwipeToggle}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {swipeEnabled
                          ? device.type === 'tablet'
                            ? 'Swipe firmly left or right to change tabs. Requires a longer swipe on tablets to prevent accidental changes.'
                            : 'Swipe left or right to navigate between tabs.'
                          : 'Use the navigation buttons or arrow keys to switch tabs.'}
                      </p>
                      {device.type === 'tablet' && (
                        <div className="pt-2 border-t">
                          <p className="text-xs text-amber-600 dark:text-amber-500">
                            <AlertCircle className="h-3 w-3 inline mr-1" />
                            Tablet detected: Swipe gestures are disabled by default to prevent
                            accidental navigation while filling the form.
                          </p>
                        </div>
                      )}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          {/* Survey Start Time */}
          {surveyStartTime && (
            <div className="flex items-center justify-end text-[10px] sm:text-xs text-muted-foreground mt-1 px-1">
              <Clock className="h-3 w-3 mr-1" />
              <span>
                Started:{' '}
                {surveyStartTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Form Content with Animations */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="py-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {activeTab === 'project' && (
                <ProjectInfo
                  form={form}
                  onPhotoUploaded={onPhotoUploaded}
                  installationId={installationId}
                  existingSurvey={
                    initialData && 'id' in initialData
                      ? { id: initialData.id as number }
                      : surveyId
                        ? { id: surveyId }
                        : null
                  }
                />
              )}
              {activeTab === 'electrical' && (
                <ElectricalSupply
                  form={form}
                  installationId={installationId}
                  surveyId={
                    surveyId ||
                    form.getValues('surveyId') ||
                    (initialData && 'id' in initialData ? (initialData.id as number) : undefined)
                  }
                  onPhotoUploaded={onPhotoUploaded}
                />
              )}
              {activeTab === 'roof' && (
                <RoofInformation
                  form={form}
                  installationId={installationId}
                  surveyId={
                    surveyId ||
                    form.getValues('surveyId') ||
                    (initialData && 'id' in initialData ? (initialData.id as number) : undefined)
                  }
                  onPhotoUploaded={onPhotoUploaded}
                />
              )}
              {activeTab === 'ac-equipment' && (
                <div className="space-y-4">
                  <AcInstallation
                    form={form}
                    installationId={installationId}
                    surveyId={
                      surveyId ||
                      form.getValues('surveyId') ||
                      (initialData && 'id' in initialData ? (initialData.id as number) : undefined)
                    }
                    onPhotoUploaded={onPhotoUploaded}
                  />
                  <EquipmentLocation
                    form={form}
                    installationId={installationId}
                    surveyId={
                      surveyId ||
                      form.getValues('surveyId') ||
                      (initialData && 'id' in initialData ? (initialData.id as number) : undefined)
                    }
                    onPhotoUploaded={onPhotoUploaded}
                  />
                </div>
              )}
              {activeTab === 'dc-works' && (
                <DcAdditionalWorks
                  form={form}
                  installationId={installationId}
                  surveyId={
                    surveyId ||
                    form.getValues('surveyId') ||
                    (initialData && 'id' in initialData ? (initialData.id as number) : undefined)
                  }
                  onPhotoUploaded={onPhotoUploaded}
                />
              )}
              {activeTab === 'summary' && <SurveySummary form={form} />}
              {activeTab === 'bom' && (
                <BillOfMaterials
                  form={form}
                  surveyId={
                    surveyId ||
                    form.getValues('surveyId') ||
                    (initialData && 'id' in initialData ? (initialData.id as number) : undefined)
                  }
                />
              )}
            </motion.div>
          </AnimatePresence>

          {/* Floating Action Bar */}
          <div className="fixed bottom-0 left-0 right-0 lg:left-[260px] bg-background/95 backdrop-blur-sm border-t px-3 py-3 sm:px-4 sm:py-4 z-20">
            <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
              {/* Navigation */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => navigateTab('prev')}
                  disabled={activeTab === tabs[0].id}
                  className="min-w-[100px]"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">Back</span>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => navigateTab('next')}
                  disabled={activeTab === tabs[tabs.length - 1].id}
                  className="min-w-[100px]"
                >
                  <span className="hidden sm:inline">Next</span>
                  <span className="sm:hidden">Next</span>
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>

              {/* Actions */}
              <div className="flex gap-2 items-center">
                {/* Mobile dropdown menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild className="sm:hidden">
                    <Button variant="outline" size="icon" className="h-8 w-8">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={handleExportPdf}>
                      <FileDown className="h-4 w-4 mr-2" />
                      Print/Export PDF
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleSave} disabled={isSaving || autoSaving}>
                      <Save className="h-4 w-4 mr-2" />
                      Save Progress
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Desktop buttons */}
                {surveyId ? (
                  <PrintToPDF
                    surveyId={surveyId.toString()}
                    highlightedFields={form.getValues('highlightedFields') || {}}
                    buttonText="PDF"
                    buttonVariant="outline"
                    buttonSize="sm"
                    className="hidden sm:flex"
                    onPrintError={(error) => {
                      toast({
                        title: 'Error',
                        description: error.message || 'Failed to generate PDF.',
                        variant: 'destructive',
                      });
                    }}
                  />
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      toast({
                        title: 'Save Required',
                        description: 'Please save the survey before exporting to PDF.',
                        variant: 'destructive',
                      });
                    }}
                    className="hidden sm:flex"
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    PDF
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleSave}
                  disabled={isSaving || isCompleting || autoSaving}
                  className="hidden sm:flex"
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save
                </Button>

                {/* Complete button - enabled when all required fields are filled */}
                <Button
                  type="button"
                  size="sm"
                  disabled={isCompleting || isSaving || !progressResult.canComplete}
                  className={cn(
                    'min-w-[100px] sm:min-w-[120px]',
                    progressResult.canComplete
                      ? 'bg-green-600 hover:bg-green-700 text-white font-semibold shadow-lg'
                      : 'opacity-50 cursor-not-allowed bg-gray-400'
                  )}
                  title={
                    !progressResult.canComplete && progressResult.missingFields.length > 0
                      ? `Missing fields: ${progressResult.missingFields.slice(0, 3).join(', ')}${progressResult.missingFields.length > 3 ? '...' : ''}`
                      : isSaving
                        ? 'Saving in progress...'
                        : 'Complete survey'
                  }
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    // Always log button click for debugging
                    console.log('[Complete Button] Click event triggered!');

                    // Double-check conditions
                    const currentCanComplete = progressResult.canComplete;
                    const currentIsSaving = isSaving;

                    console.log('[Complete Button] Current state:', {
                      currentCanComplete,
                      currentIsSaving,
                      percentage: progressResult.percentage,
                      formIsValid: form.formState.isValid,
                      formErrors: form.formState.errors,
                    });

                    // If button is disabled, don't proceed
                    if (currentIsSaving || !currentCanComplete) {
                      console.log('[Complete Button] Button is disabled, not proceeding');
                      toast({
                        title: 'Cannot Complete',
                        description: currentIsSaving
                          ? 'Please wait, survey is being saved...'
                          : 'Please fill all required fields first',
                        variant: 'destructive',
                      });
                      return;
                    }

                    try {
                      // Get current form values
                      const formData = form.getValues();
                      console.log('[Complete Button] Submitting with data:', formData);

                      // Call onComplete directly if form.handleSubmit isn't working
                      if (onComplete) {
                        console.log('[Complete Button] Calling onComplete directly...');
                        await onComplete(formData);
                      } else {
                        console.error('[Complete Button] onComplete prop is missing!');
                        toast({
                          title: 'Error',
                          description: 'Survey completion handler is not configured',
                          variant: 'destructive',
                        });
                      }
                    } catch (error) {
                      console.error('[Complete Button] Error during submission:', error);
                      toast({
                        title: 'Error',
                        description: 'Failed to complete survey. Please try again.',
                        variant: 'destructive',
                      });
                    }
                  }}
                >
                  {isCompleting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      <span className="hidden sm:inline">Completing...</span>
                      <span className="sm:hidden">...</span>
                    </>
                  ) : !progressResult.canComplete ? (
                    <>
                      <span className="hidden sm:inline">
                        Complete ({progressResult.percentage}%)
                      </span>
                      <span className="sm:hidden">{progressResult.percentage}%</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      <span className="hidden sm:inline">Complete Survey</span>
                      <span className="sm:hidden">Done</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Add padding to account for floating action bar */}
          <div className="h-20" />
        </form>
      </Form>

      {/* Visual Swipe Indicators for Touch Devices */}
      {device.isTouch && swipeEnabled && !isFormInteracting && !isScrolling && (
        <>
          {/* Left swipe indicator - show if not on first tab */}
          {activeTab !== tabs[0].id && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 0.3, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              className="fixed left-2 top-1/2 transform -translate-y-1/2 pointer-events-none z-10"
            >
              <div className="flex items-center gap-1 text-muted-foreground">
                <ChevronLeft className="h-8 w-8 text-gray-400/50" />
                {device.type === 'tablet' && (
                  <span className="text-xs text-gray-400/50 writing-mode-vertical">
                    Swipe
                  </span>
                )}
              </div>
            </motion.div>
          )}

          {/* Right swipe indicator - show if not on last tab */}
          {activeTab !== tabs[tabs.length - 1].id && (
            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 0.3, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="fixed right-2 top-1/2 transform -translate-y-1/2 pointer-events-none z-10"
            >
              <div className="flex items-center gap-1 text-muted-foreground">
                {device.type === 'tablet' && (
                  <span className="text-xs text-gray-400/50 writing-mode-vertical">
                    Swipe
                  </span>
                )}
                <ChevronRight className="h-8 w-8 text-gray-400/50" />
              </div>
            </motion.div>
          )}
        </>
      )}
    </div>
  );
}
